import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { InputType } from 'enums/InputType';
import { CustomPropertyType, QueryPropertyModel, QueryPropertyType, QueryPropertyValueType } from 'models/generated/Models.QueryProperty';
import { SelectListItemModel } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserService } from 'services/user.service';
import { QueryService } from 'services/query.service';
import { QueryType } from 'enums/QueryType';
import { QueryInputOptionsService } from './query-input-options.service';

@Component({
	selector: 'query-input',
	templateUrl: './query-input.component.html',
	imports: [CustomInputComponent, DateInputComponent, CustomSelectComponent, TranslatePipe]
})
export class QueryInputComponent {

	@Output() valueChange = new EventEmitter<any>();

	inputTypeEnum = InputType;
	queryPropertyType = QueryPropertyType;
	queryPropertyValueType = QueryPropertyValueType;
	queryCustomType = CustomPropertyType;

	selectList: SelectListItemModel[] = [];
	usersList: SelectListItemModel[] = [];
	taskTemplateTagList: SelectListItemModel[] = [];
	taskflowTemplateTagList: SelectListItemModel[] = [];
	userTeamList: SelectListItemModel[] = [];
	savedQueryList: SelectListItemModel[] = [];
	reasonList: SelectListItemModel[] = [];
	productPlanTagList: SelectListItemModel[] = [];
	productTagList: SelectListItemModel[] = [];
	taskAreaList: SelectListItemModel[] = [];
	booleanList: SelectListItemModel[];

	_propertyName = '';

	private _value: any;
	dateValue?: Date;
	@Input()
	set value(value: any) {
		this._value = value;
		this.dateValue = value ? new Date(value) : undefined;
	}
	get value() {
		return this._value;
	}

	@Input()
	set options(options: { [key: string]: string }) {
		this.selectList = [];
		for (let key in options) {
			if (options.hasOwnProperty(key) && key != '$id') {
				this.selectList.push(new SelectListItemModel(key, options[key]));
			}
		}
	}

	private _queryProperties: QueryPropertyModel[] =[];
	@Input()
	set queryProperties(queryProperties: QueryPropertyModel[]) {
		this._queryProperties = queryProperties;
		this.loadQueryProperty();
	}
	get queryProperties() {
		return this._queryProperties;
	}
	@Input() queryProperty?: QueryPropertyModel;

	@Input()
	set propertyName(name: string) {
		this._propertyName = name;
		this.loadQueryProperty();
	}
	get propertyName() {
		return this._propertyName;
	}

	private readonly userService = inject(UserService);
	/** Provided by the list of inputs this input belongs to, which shares the options between its inputs. */
	private readonly inputOptions = inject(QueryInputOptionsService);

	constructor(translate: TranslatePipe) {
		this.booleanList = [new SelectListItemModel('true', translate.transform('general.true')), new SelectListItemModel('false', translate.transform('general.false'))];
	}
	
	updateValue(value: any) {
		if (this.queryProperty?.type == QueryPropertyType.Select) value = QueryService.toSelectValue(value);
		if (this.queryProperty?.valueType == QueryPropertyValueType.Boolean && typeof value == 'string') value = value == 'true';
		this.value = value;
		this.valueChange.emit(value);
	}

	async loadQueryProperty() {
		if (!this.propertyName || !this.queryProperties) return;

		this.queryProperty = this.queryProperties!.find(queryProperty => queryProperty.name.toLowerCase() == this.propertyName.toLowerCase());

		this.usersList = [];
		this.taskTemplateTagList = [];
		this.taskflowTemplateTagList = [];
		this.userTeamList = [];
		this.savedQueryList = [];
		this.reasonList = [];
		this.productPlanTagList = [];
		this.productTagList = [];
		this.taskAreaList = [];

		if (!this.queryProperty) return;

		this.usersList = this.userService.getUserSelectList();

		switch (this.queryProperty.type) {
			case QueryPropertyType.InputTeamId:
				this.userTeamList = await this.inputOptions.getTeamList();
				break;
			case QueryPropertyType.InputTaskAreaId:
				this.taskAreaList = await this.inputOptions.getTaskAreaList();
				break;
			case QueryPropertyType.InputReasonId:
				this.reasonList = await this.inputOptions.getReasonList();
				break;
			case QueryPropertyType.Query:
				this.savedQueryList = await this.inputOptions.getSavedQueryList(QueryType[this.queryProperty.queryType as keyof typeof QueryType] as QueryType);
				break;
		}

		switch (this.queryProperty.customProperty) {
			case CustomPropertyType.ProductPlanTag:
				this.productPlanTagList = await this.inputOptions.getProductPlanTagList();
				break;
			case CustomPropertyType.ProductTag:
			case CustomPropertyType.OrderLineProductTag:
				this.productTagList = await this.inputOptions.getProductTagList();
				break;
			case CustomPropertyType.TaskTemplateTag:
				this.taskTemplateTagList = await this.inputOptions.getTaskTemplateTagList();
				break;
			case CustomPropertyType.TaskflowTemplateTag:
				this.taskflowTemplateTagList = await this.inputOptions.getTaskflowTemplateTagList();
				break;
		}
	}

}
